// The three dashboards (Owner, Operations / truck board, Fuel) and Home, which opens the right one
// for the person's role.
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Page, Card, Table, Tr, Td, Loading, Pill } from "@/components/kit";
import { Alerts, Period, Tile, State, big, k0, pct, thisMonth, useCan, useLoad } from "@/components/erp";
import { EarnedVsCosts, ProfitPerTruck } from "@/components/charts";

export function Home() {
  const can = useCan();
  if (can("dash.owner")) return <OwnerDash />;
  if (can("dash.ops")) return <OpsDash />;
  if (can("fuel.issue", "fuel.dip", "fuel.receive")) return <Navigate to="/depot" replace />;
  if (can("trips.view")) return <Navigate to="/trips" replace />;
  return <Navigate to="/approvals" replace />;
}

const profitTone = (v) => (v < 0 ? "text-rose-700" : "");

export function OwnerDash() {
  const [p, setP] = useState(thisMonth());
  const { data: d } = useLoad("/dash/owner", p);
  return (
    <Page title="Owner dashboard">
      <Period value={p} onChange={setP} />
      {!d ? <Loading /> : (<>
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Tile label="Earned (delivered trips)" value={big(d.totals.revenue)} exact={d.totals.revenue} sub={`${d.totals.trips} trips · ${k0(d.totals.in_transit)} in transit`} />
          <Tile label="Fuel" value={big(d.totals.fuel)} exact={d.totals.fuel} sub={`${pct(d.totals.fuel_pct)} of earnings`} />
          <Tile label="Other costs" value={big(d.totals.other)} exact={d.totals.other} />
          <Tile label="Hire purchase" value={big(d.totals.hire_purchase)} exact={d.totals.hire_purchase} />
          <Tile label="Profit" value={big(d.totals.profit)} exact={d.totals.profit} sub={`margin ${pct(d.totals.margin_pct)}`} tone={profitTone(d.totals.profit)} />
          <Tile label="M-Pesa float" value={big(d.cash.float.balance)} exact={d.cash.float.balance} sub={`${k0(d.cash.with_staff)} with staff · ${d.cash.approvals_pending} awaiting approval`} />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
          <Tile label="Revenue / truck / month" value={big(d.kpis.revenue_per_truck_month)} exact={d.kpis.revenue_per_truck_month} sub={`${d.kpis.trucks} trucks`} />
          <Tile label="Utilisation" value={pct(d.kpis.utilisation_pct)} sub="days on a trip" tone={(d.kpis.utilisation_pct ?? 100) < 60 ? "text-rose-700" : ""} />
          <Tile label="Fleet availability" value={pct(d.kpis.availability_pct)} sub="not in workshop / parked" />
          <Tile label="Trips / truck / month" value={d.kpis.trips_per_truck_month ?? "—"} />
          <Tile label="Maintenance / truck" value={big(d.kpis.maintenance_per_truck)} exact={d.kpis.maintenance_per_truck} />
          <Tile label="Revenue / cost per km" value={d.kpis.revenue_per_km ? `${k0(d.kpis.revenue_per_km)} / ${k0(d.kpis.cost_per_km)}` : "—"}
            sub={d.kpis.km ? `${k0(d.kpis.km)} km recorded` : "needs odometer readings"} />
          <Tile label="Shortages" value={pct(d.kpis.shortages_pct)} sub="of what was billed" tone={(d.kpis.shortages_pct || 0) > 2 ? "text-rose-700" : ""} />
        </div>
        <Card title="Needs attention" className="mt-5 bg-white"><Alerts alerts={d.alerts} /></Card>
        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <Card title="Earned and costs, month by month" className="bg-white"><EarnedVsCosts months={d.by_month} /></Card>
          <Card title="Profit per truck" className="bg-white"><ProfitPerTruck rows={d.by_truck} /></Card>
        </div>
        <Card title="Per truck in detail" className="mt-5 bg-white">
          <Table minWidth={820} cols={[{ label: "Truck" }, { label: "Trips", align: "right" }, { label: "Earned", align: "right" }, { label: "Fuel", align: "right" },
            { label: "Fuel %", align: "right" }, { label: "Other", align: "right" }, { label: "HP", align: "right" }, { label: "Profit", align: "right" }, { label: "Margin", align: "right" }, { label: "Utilisation", align: "right" }]}>
            {d.by_truck.map((r) => (
              <Tr key={r.truck_id}><Td className="font-medium">{r.plate}</Td><Td right>{r.trips}</Td><Td right>{k0(r.revenue)}</Td><Td right>{k0(r.fuel)}</Td>
                <Td right>{pct(r.fuel_pct)}</Td><Td right>{k0(r.other)}</Td><Td right>{k0(r.hire_purchase)}</Td>
                <Td right className={`font-semibold ${profitTone(r.profit)}`}>{k0(r.profit)}</Td><Td right className={profitTone(r.profit)}>{pct(r.margin_pct)}</Td><Td right>{pct(r.utilisation_pct)}</Td></Tr>
            ))}
          </Table>
        </Card>
        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <Card title="Per contract" className="bg-white">
            <Table minWidth={520} cols={[{ label: "Contract" }, { label: "Trips", align: "right" }, { label: "Tonnes", align: "right" }, { label: "Earned", align: "right" }, { label: "Shortages", align: "right" }]}>
              {d.by_contract.map((c) => (
                <Tr key={c.contract}><Td>{c.contract}</Td><Td right>{c.trips}</Td><Td right>{k0(c.tonnes)}</Td><Td right>{k0(c.revenue)}</Td>
                  <Td right className={(c.shortage_pct || 0) > 2 ? "text-rose-700" : ""}>{k0(c.shortages)} {c.shortage_pct ? `(${pct(c.shortage_pct)})` : ""}</Td></Tr>
              ))}
            </Table>
          </Card>
          <Card title="Month by month" className="bg-white">
            <Table minWidth={520} cols={[{ label: "Month" }, { label: "Earned", align: "right" }, { label: "Fuel", align: "right" }, { label: "Other", align: "right" }, { label: "Profit", align: "right" }, { label: "Margin", align: "right" }]}>
              {d.by_month.map((m) => (
                <Tr key={m.month}><Td>{m.month}</Td><Td right>{k0(m.revenue)}</Td><Td right>{k0(m.fuel)}</Td><Td right>{k0(m.other)}</Td>
                  <Td right className={profitTone(m.profit)}>{k0(m.profit)}</Td><Td right className={(m.margin_pct ?? 100) < 10 ? "text-rose-700" : ""}>{pct(m.margin_pct)}</Td></Tr>
              ))}
            </Table>
            <h3 className="mt-5 text-sm font-semibold">Costs by kind</h3>
            <ul className="mt-2 space-y-1 text-sm">{Object.entries(d.costs_by_group).map(([g, v]) => <li key={g} className="flex justify-between"><span className="capitalize">{g}</span><span className="tabular-nums">{k0(v)}</span></li>)}</ul>
          </Card>
        </div>
      </>)}
    </Page>
  );
}

const STATE = { idle: ["Idle", "red"], dispatched: ["On its way to load", "blue"], loaded: ["Loaded", "amber"], workshop: ["Workshop", "grey"], parked: ["Parked", "grey"] };

export function OpsDash() {
  const [p, setP] = useState(thisMonth());
  const { data: d } = useLoad("/dash/ops", p);
  return (
    <Page title="Truck board">
      {!d ? <Loading /> : (<>
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
          {Object.entries(STATE).map(([k, [l]]) => <Tile key={k} label={l} value={d.counts[k] || 0} tone={k === "idle" && d.counts.idle ? "text-rose-700" : ""} />)}
        </div>
        {d.alerts.length > 0 && <Card title="Needs attention" className="mt-5 bg-white"><Alerts alerts={d.alerts} /></Card>}
        <Card title="Every truck now" className="mt-5 bg-white">
          <Table minWidth={760} cols={[{ label: "Truck" }, { label: "State" }, { label: "Days" }, { label: "Trip / route" }, { label: "Driver" }, { label: "Documents" }]}>
            {d.board.map((b) => {
              const [l, kind] = STATE[b.state] || [b.state, "grey"];
              return (
                <Tr key={b.truck_id}>
                  <Td className="font-medium">{b.plate}</Td>
                  <Td><Pill kind={kind}>{l}</Pill></Td>
                  <Td className={b.state === "idle" && b.days > 3 ? "font-semibold text-rose-700" : ""}>{b.days ?? "—"}</Td>
                  <Td>{b.trip ? <Link to={`/trips/${b.trip.id}`} className="text-sky-700 hover:underline">{b.trip.no}</Link> : ""} {b.trip?.route || b.trip?.contract || ""}</Td>
                  <Td>{b.driver}</Td>
                  <Td>{b.documents.map((x, i) => <Pill key={i} kind={x.status === "expired" ? "red" : "amber"}>{x.kind} {x.status === "expired" ? "expired" : `${x.days_left}d`}</Pill>)}</Td>
                </Tr>
              );
            })}
          </Table>
        </Card>
        <Period value={p} onChange={setP} />
        <Card title="Turnaround (loading to delivery)" className="mt-3 bg-white">
          <Table minWidth={420} cols={[{ label: "Route" }, { label: "Trips", align: "right" }, { label: "Average days", align: "right" }, { label: "Longest", align: "right" }]}>
            {d.turnaround.map((t) => <Tr key={t.route}><Td>{t.route}</Td><Td right>{t.trips}</Td><Td right>{t.avg_days}</Td><Td right>{t.max_days}</Td></Tr>)}
          </Table>
          <p className="mt-3 text-sm text-slate-600">Trips this period: {Object.entries(d.trips_by_status).map(([s, c]) => <span key={s} className="mr-3"><State s={s} /> {c}</span>)}</p>
        </Card>
      </>)}
    </Page>
  );
}

export function FuelDash() {
  const [p, setP] = useState(thisMonth());
  const { data: d } = useLoad("/dash/fuel", p);
  return (
    <Page title="Fuel analysis">
      <Period value={p} onChange={setP} />
      {!d ? <Loading /> : (<>
        <Card title="Each truck" className="mt-4 bg-white">
          <p className="mt-1 text-sm text-slate-600">Litres per 100 km from the odometer at each fill (fleet typical: {d.fleet_l_per_100km ?? "—"}); litres per trip against trucks mostly on the same contract.</p>
          <Table minWidth={900} cols={[{ label: "Truck" }, { label: "Litres", align: "right" }, { label: "Km", align: "right" }, { label: "L/100 km", align: "right" }, { label: "vs fleet", align: "right" },
            { label: "Trips", align: "right" }, { label: "L/trip", align: "right" }, { label: "vs peers", align: "right" }, { label: "Main contract" }, { label: "Outside premium", align: "right" }, { label: "Flags", align: "right" }]}>
            {d.trucks.map((t) => (
              <Tr key={t.truck_id} className={t.flag || (t.vs_fleet_pct || 0) > 10 ? "bg-rose-50" : ""}>
                <Td className="font-medium">{t.plate}</Td><Td right>{k0(t.litres)}</Td><Td right>{k0(t.km)}</Td><Td right>{t.l_per_100km ?? "—"}</Td><Td right>{pct(t.vs_fleet_pct)}</Td>
                <Td right>{t.trips}</Td><Td right>{k0(t.l_per_trip)}</Td><Td right className={t.flag ? "font-semibold text-rose-700" : ""}>{pct(t.vs_peers_pct)}</Td>
                <Td>{t.main_contract}</Td><Td right>{k0(t.outside_premium)}</Td><Td right>{t.flags || ""}</Td>
              </Tr>
            ))}
          </Table>
        </Card>
        <Card title="Trips against the route's range and the truck's own norm" className="mt-5 bg-white">
          <Table minWidth={760} cols={[{ label: "Trip" }, { label: "Truck" }, { label: "Route" }, { label: "Litres", align: "right" }, { label: "Route range", align: "right" }, { label: "Own norm", align: "right" }, { label: "" }]}>
            {d.trips.map((t) => (
              <Tr key={t.trip_id} className={t.flags.length ? "bg-rose-50" : ""}>
                <Td><Link to={`/trips/${t.trip_id}`} className="text-sky-700 hover:underline">{t.no}</Link></Td><Td>{t.plate}</Td><Td>{t.route}</Td><Td right>{k0(t.litres)}</Td>
                <Td right>{t.route_min ? `${k0(t.route_min)}–${k0(t.route_max)}` : "—"}</Td><Td right>{k0(t.own_norm)}</Td><Td className="text-rose-700">{t.flags.join("; ")}</Td>
              </Tr>
            ))}
          </Table>
        </Card>
        <DepotTable rows={d.depot} />
        {d.issues_flagged.length > 0 && (
          <Card title="Flagged fuel issues" className="mt-5 bg-white">
            <ul className="mt-2 space-y-1 text-sm">{d.issues_flagged.map((f) => <li key={f.id}><b>{f.date} · {f.plate}</b> {f.litres} L — {f.flags.join("; ")}</li>)}</ul>
          </Card>
        )}
      </>)}
    </Page>
  );
}

export function DepotTable({ rows }) {
  const tone = { ok: "green", warning: "amber", critical: "red", no_dip: "grey" };
  return (
    <Card title="Depot stock check" className="mt-5 bg-white">
      <Table minWidth={760} cols={[{ label: "Tank" }, { label: "Opening", align: "right" }, { label: "Received", align: "right" }, { label: "Issued", align: "right" },
        { label: "Book", align: "right" }, { label: "Last dip", align: "right" }, { label: "Gap", align: "right" }, { label: "" }, { label: "Cost / L", align: "right" }]}>
        {(rows || []).map((t) => (
          <Tr key={t.tank_id}><Td className="font-medium">{t.tank}</Td><Td right>{k0(t.opening_l)}</Td><Td right>{k0(t.received_l)}</Td><Td right>{k0(t.issued_l)}</Td>
            <Td right>{k0(t.book_l)}</Td><Td right>{t.closing_l == null ? "—" : k0(t.closing_l)}</Td><Td right>{t.gap_l == null ? "—" : `${k0(t.gap_l)} L (${pct(t.gap_pct)})`}</Td>
            <Td><Pill kind={tone[t.status]}>{t.status === "no_dip" ? "No dip" : t.status}</Pill></Td><Td right>{t.cost_per_l || "—"}</Td></Tr>
        ))}
      </Table>
    </Card>
  );
}
